import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {OrganizationalUnit} from '../types/OrganizationalUnit';
import organizationalUnitsService from '../services/organizationalUnitsService';
import {handleError} from '../../../common/utils/errorHandling';
import {getParentOptions} from '../../../common/utils/dropdownUtils';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import AdminFormHeader from "../../../layout/admin/AdminFormHeader";
import {AdminButton, AdminForm, AdminFormSection, AdminInputDropdown, AdminInputText} from "../../../layout/admin";
import {DEFAULT_ORGANIZATIONAL_UNIT_PRESET, useOrganizationalUnitPresets} from "../constants/presets";
import {useFormErrorHandling} from "../../../common/hooks/useFormErrorHandling";

const OrganizationalUnitsForm: React.FC = () => {
  const {t} = useTranslation();
  const presets = useOrganizationalUnitPresets();
  const {errors, handleApiError, clearErrors} = useFormErrorHandling();
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    parentId: ''
  });

  const {id} = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const [unit, setUnit] = useState<OrganizationalUnit | undefined>();
  const [allUnits, setAllUnits] = useState<OrganizationalUnit[]>([]);
  const [activePreset, setActivePreset] = useState<string | undefined>();
  const navigate = useNavigate();

  const preset = searchParams.get('preset') || DEFAULT_ORGANIZATIONAL_UNIT_PRESET;

  useEffect(() => {
    fetchAllUnits();
    if (id) {
      fetchUnit(id);
    } else {
      // Set active preset for new units
      setActivePreset(preset);
    }
  }, [id, preset]);

  const fetchAllUnits = async (): Promise<void> => {
    await handleError(
      async () => {
        const unitsData = await organizationalUnitsService.getAllOrganizationalUnits();
        const filteredUnits = unitsData.filter(u => u.hierarchyType === preset);
        setAllUnits(filteredUnits);
      },
      t('admin.organizationalUnits.errors.fetching')
    );
  };

  const fetchUnit = async (unitId: string): Promise<void> => {
    await handleError(
      async () => {
        const unitData = await organizationalUnitsService.getOrganizationalUnit(unitId);
        setUnit(unitData);
        setFormData({
          name: unitData.name,
          type: unitData.type,
          parentId: unitData.parent?.id || ''
        });
        setActivePreset(unitData.hierarchyType);
      },
      t('admin.organizationalUnits.errors.fetchingUnit')
    );
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {name, value} = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDropdownChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      parentId: e.target.value
    }));
  };

  const handlePresetChange = (alias: string) => {
    if (unit) {
      return;
    }
    setActivePreset(alias);
    setFormData(prev => ({
      ...prev,
      type: ''
    }));
  };

  const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      type: e.target.value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearErrors();

    try {
      const currentHierarchyType = activePreset || preset;

      if (unit) {
        await organizationalUnitsService.updateOrganizationalUnit({
          ...unit,
          name: formData.name,
          type: formData.type,
          hierarchyType: currentHierarchyType,
          parentId: formData.parentId
        });
      } else {
        await organizationalUnitsService.createOrganizationalUnit({
          name: formData.name,
          type: formData.type,
          hierarchyType: currentHierarchyType,
          parentId: formData.parentId
        });
      }
      navigate(`/organizational-units?preset=${activePreset}`);
    } catch (error) {
      handleApiError(error);
    }
  };

  const typeOptions = (activePreset || preset) === 'church' ? [
    {value: 'diocese', label: t('places.filters.church.diocese')},
    {value: 'deanery', label: t('places.filters.church.deanery')},
    {value: 'parish', label: t('places.filters.church.parish')}
  ] : [
    {value: 'voivodeship', label: t('places.filters.administrative.voivodeship')},
    {value: 'county', label: t('places.filters.administrative.county')},
    {value: 'municipality', label: t('places.filters.administrative.municipality')}
  ];

  const parentOptions = getParentOptions(
    allUnits,
    unit,
    (u) => `${u.name} (${u.type})`
  );

  return (
    <>
      <AdminFormHeader
        title={unit ? t('admin.organizationalUnits.title.edit') : t('admin.organizationalUnits.title.create')}
        subtitle={unit ? t('admin.organizationalUnits.subtitle.edit') : t('admin.organizationalUnits.subtitle.create')}
        presets={presets}
        activePreset={activePreset}
        canChangePreset={!unit}
        onPresetChange={handlePresetChange}
      />

      <AdminForm
        leftColumn={
          <>
            <AdminFormSection
              title={t('admin.common.sections.basicInfo')}
            >
              <AdminInputText
                label={t('admin.common.fields.name')}
                value={formData.name}
                onChange={handleInputChange}
                name="name"
                id="name"
                required={true}
                error={errors.name}
              />
              <AdminInputDropdown
                label={t('admin.common.columns.type')}
                value={formData.type}
                onChange={handleTypeChange}
                options={typeOptions}
                placeholder={t('admin.organizationalUnits.placeholders.selectType')}
                name="type"
                id="type"
                required={true}
                error={errors.type}
              />
              <AdminInputDropdown
                label={t('admin.organizationalUnits.fields.parentUnit')}
                value={formData.parentId}
                onChange={handleDropdownChange}
                options={parentOptions}
                placeholder={t('admin.organizationalUnits.placeholders.selectParent')}
                name="parentId"
                id="parentId"
                error={errors.parentId}
              />
            </AdminFormSection>
          </>
        }
        rightColumn={
          <>
            <AdminFormSection
              title={t('admin.organizationalUnits.sections.hierarchyInfo')}
            >
              <div className="text-sm text-gray-600">
                <p>{t('admin.organizationalUnits.help.hierarchy1')}</p>
                <p>{t('admin.organizationalUnits.help.hierarchy2')}</p>
              </div>
            </AdminFormSection>
          </>
        }
        formActions={
          <>
            <AdminButton
              type="button"
              label={t('admin.common.actions.cancel')}
              onClick={() => navigate(`/organizational-units?preset=${activePreset}`)}
            />
            <AdminButton
              type="submit"
              label={unit ? t('admin.common.actions.update') : t('admin.common.actions.create')}
              variant="black"
            />
          </>
        }
        onSubmit={handleSubmit}
      />
    </>
  );
};

export default OrganizationalUnitsForm;

